<template>
  <div class="register-container">
    <div class="register-form-container">
      <!-- Logo Section -->
      <div class="logo-section">
        <div class="logo-wrapper">
          <img src="@/assets/logo.png" alt="QUALI-D" class="logo" />
        </div>
        <p class="brand-subtitle">Measure, Improve, Succeed</p>
      </div>

      <!-- Register Form -->
      <div class="form-section">
        <div class="form-header">
          <h2 class="form-title">Create Account</h2>
        </div>

        <form @submit.prevent="handleRegister" class="register-form">
          <!-- Personal Information -->
          <div class="form-section-title">Personal Information</div>

          <div class="form-group">
            <label for="name" class="form-label">Full Name</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <input
                id="name"
                v-model="registerForm.name"
                type="text"
                placeholder="Enter your full name"
                class="form-input"
                :class="{ 'error': errors.name }"
                required
              />
            </div>
            <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
          </div>

          <div class="form-group">
            <label for="companyName" class="form-label">Company Name</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 21h18"/>
                <path d="M5 21V7l8-4v18"/>
                <path d="M19 21V11l-6-4"/>
              </svg>
              <input
                id="companyName"
                v-model="registerForm.companyName"
                type="text"
                placeholder="Enter your company name"
                class="form-input"
                :class="{ 'error': errors.companyName }"
                required
              />
            </div>
            <span v-if="errors.companyName" class="error-message">{{ errors.companyName }}</span>
          </div>

          <div class="form-group">
            <label for="email" class="form-label">Email Address</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <input
                id="email"
                v-model="registerForm.email"
                type="email"
                placeholder="Enter your email address"
                class="form-input"
                :class="{ 'error': errors.email }"
                required
              />
            </div>
            <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
          </div>

          <div class="form-group">
            <label for="phone" class="form-label">Phone Number</label>
            <div class="phone-input-group">
              <CountryCodeSelector
                v-model="registerForm.countryCode"
                :has-error="!!errors.countryCode"
              />
              <div class="input-wrapper phone-input-wrapper">
                <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
                <input
                  id="phone"
                  v-model="registerForm.phone"
                  type="tel"
                  placeholder="Enter WhatsApp phone number"
                  class="form-input"
                  :class="{ 'error': errors.phone }"
                  required
                />
              </div>
            </div>
            <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
            <span v-if="errors.countryCode" class="error-message">{{ errors.countryCode }}</span>
            <p class="field-note">Phone number must have an active WhatsApp account</p>
          </div>

          <!-- Password Section -->
          <div class="form-section-title">Security</div>

          <div class="form-group">
            <label for="password" class="form-label">Password</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <circle cx="12" cy="7" r="4"/>
                <path d="m8 11 0-4a4 4 0 0 1 8 0v4"/>
              </svg>
              <input
                id="password"
                v-model="registerForm.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Enter your password"
                class="form-input"
                :class="{ 'error': errors.password }"
                required
              />
              <button
                type="button"
                @click="togglePassword"
                class="toggle-password"
              >
                <svg v-if="!showPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                  <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                  <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              </button>
            </div>
            <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
            <div class="password-requirements">
              <p class="requirements-title">Password must be at least 8 characters long and include:</p>
              <ul class="requirements-list">
                <li :class="{ 'valid': passwordChecks.hasUppercase }">
                  <span class="check-icon">{{ passwordChecks.hasUppercase ? '✓' : '×' }}</span>
                  At least one uppercase letter (A-Z)
                </li>
                <li :class="{ 'valid': passwordChecks.hasLowercase }">
                  <span class="check-icon">{{ passwordChecks.hasLowercase ? '✓' : '×' }}</span>
                  At least one lowercase letter (a-z)
                </li>
                <li :class="{ 'valid': passwordChecks.hasNumber }">
                  <span class="check-icon">{{ passwordChecks.hasNumber ? '✓' : '×' }}</span>
                  At least one number (0-9)
                </li>
                <li :class="{ 'valid': passwordChecks.hasMinLength }">
                  <span class="check-icon">{{ passwordChecks.hasMinLength ? '✓' : '×' }}</span>
                  At least 8 characters long
                </li>
              </ul>
            </div>
          </div>

          <div class="form-group">
            <label for="confirmPassword" class="form-label">Confirm Password</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <circle cx="12" cy="7" r="4"/>
                <path d="m8 11 0-4a4 4 0 0 1 8 0v4"/>
              </svg>
              <input
                id="confirmPassword"
                v-model="registerForm.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Confirm your password"
                class="form-input"
                :class="{ 'error': errors.confirmPassword }"
                required
              />
              <button
                type="button"
                @click="toggleConfirmPassword"
                class="toggle-password"
              >
                <svg v-if="!showConfirmPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                  <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                  <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              </button>
            </div>
            <span v-if="errors.confirmPassword" class="error-message">{{ errors.confirmPassword }}</span>
          </div>

          <!-- Plans Section -->
          <div class="form-section-title">Choose Your Plan</div>

          <!-- Plan Type Toggle -->
          <div v-if="!loadingPlans && !plansError" class="plan-toggle-container">
            <button
              type="button"
              class="plan-toggle-button"
              :class="{ 'active': selectedPlanType === 'monthly' }"
              @click="selectedPlanType = 'monthly'"
            >
              Monthly
            </button>
            <button
              type="button"
              class="plan-toggle-button"
              :class="{ 'active': selectedPlanType === 'annual' }"
              @click="selectedPlanType = 'annual'"
            >
              <span>Annual</span>
              <span class="toggle-badge">Save 20%</span>
            </button>
          </div>

          <div v-if="loadingPlans" class="loading-plans">
            <div class="spinner"></div>
            <span>Loading plans...</span>
          </div>

          <div v-else-if="plansError" class="plans-error">
            <p>{{ plansError }}</p>
            <button type="button" @click="fetchPlans" class="retry-button">Retry</button>
          </div>

          <div v-else class="plans-grid">
            <div
              v-for="plan in filteredPlans"
              :key="plan.id"
              class="plan-card"
              :class="{
                'selected': registerForm.selectedPlan === plan.id,
                'annual-plan': plan.plan_type === 'annual'
              }"
              @click="selectPlan(plan.id)"
            >
              <!-- Annual Badge -->
              <div v-if="plan.plan_type === 'annual'" class="annual-badge">
                <span class="star-icon">⭐</span>
                <span class="badge-text">20% OFF</span>
              </div>

              <div class="plan-header">
                <h3 class="plan-name">{{ plan.name }}</h3>
                <div v-if="plan.plan_type === 'annual'" class="price-wrapper">
                  <div class="monthly-price">
                    <span class="price-amount">{{ Math.round(plan.price / 12).toLocaleString() }}</span>
                    <span class="price-currency">EGP/mo</span>
                  </div>
                  <div class="annual-price">
                    {{ plan.price.toLocaleString() }} EGP billed annually
                  </div>
                </div>
                <div v-else class="plan-price">{{ plan.price.toLocaleString() }} EGP</div>
              </div>
              <div class="plan-details">
                <div class="plan-detail">
                  <span class="detail-label">Agents:</span>
                  <span class="detail-value">{{ plan.agents_limit == 0 ? '∞' : plan.agents_limit }}</span>
                </div>
                <div class="plan-detail">
                  <span class="detail-label">Credits:</span>
                  <span class="detail-value">{{ plan.credit_amount == 0 ? '∞' : plan.credit_amount.toLocaleString() }}</span>
                </div>
                <div class="plan-detail">
                  <span class="detail-label">Products:</span>
                  <span class="detail-value">{{ plan.products_services_limit == 0 ? '∞' : plan.products_services_limit.toLocaleString() }}</span>
                </div>
              </div>
            </div>
          </div>
          <span v-if="errors.selectedPlan" class="error-message">{{ errors.selectedPlan }}</span>

          <!-- Plan Summary -->
          <div v-if="selectedPlanDetails && !loadingPlans && !plansError" class="plan-summary">
            <div class="summary-header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
              <span>Your Selected Plan</span>
            </div>
            <div class="summary-content">
              <div class="summary-left">
                <h4 class="summary-plan-name">{{ selectedPlanDetails.name }}</h4>
                <div class="summary-billing">
                  <span v-if="selectedPlanDetails.plan_type === 'annual'" class="billing-info">
                    {{ Math.round(selectedPlanDetails.price / 12).toLocaleString() }} EGP/month • Billed {{ selectedPlanDetails.price.toLocaleString() }} EGP annually
                  </span>
                  <span v-else class="billing-info">
                    {{ selectedPlanDetails.price.toLocaleString() }} EGP/month
                  </span>
                </div>
              </div>
              <div class="summary-right">
                <div class="summary-price">
                  <span class="total-label">Total</span>
                  <span class="total-amount">{{ selectedPlanDetails.price.toLocaleString() }} EGP</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Section -->
          <div class="form-section-title">Payment Information</div>

          <div class="form-group">
            <label for="cardNumber" class="form-label">Card Number</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                <line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
              <input
                id="cardNumber"
                v-model="registerForm.cardNumber"
                type="text"
                placeholder="1234 5678 9012 3456"
                class="form-input"
                :class="{ 'error': errors.cardNumber }"
                maxlength="19"
                @input="formatCardNumber"
                required
              />
            </div>
            <span v-if="errors.cardNumber" class="error-message">{{ errors.cardNumber }}</span>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="cardMonth" class="form-label">Expiry Month</label>
              <div class="input-wrapper">
                <select
                  id="cardMonth"
                  v-model="registerForm.cardMonth"
                  class="form-input"
                  :class="{ 'error': errors.cardMonth }"
                  required
                >
                  <option value="">Month</option>
                  <option v-for="month in months" :key="month.value" :value="month.value">
                    {{ month.label }}
                  </option>
                </select>
              </div>
              <span v-if="errors.cardMonth" class="error-message">{{ errors.cardMonth }}</span>
            </div>

            <div class="form-group">
              <label for="cardYear" class="form-label">Expiry Year</label>
              <div class="input-wrapper">
                <select
                  id="cardYear"
                  v-model="registerForm.cardYear"
                  class="form-input"
                  :class="{ 'error': errors.cardYear }"
                  required
                >
                  <option value="">Year</option>
                  <option v-for="year in years" :key="year.value" :value="year.value">
                    {{ year.label }}
                  </option>
                </select>
              </div>
              <span v-if="errors.cardYear" class="error-message">{{ errors.cardYear }}</span>
            </div>

            <div class="form-group">
              <label for="cardCvv" class="form-label">CVV</label>
              <div class="input-wrapper">
                <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <circle cx="12" cy="7" r="4"/>
                  <path d="m8 11 0-4a4 4 0 0 1 8 0v4"/>
                </svg>
                <input
                  id="cardCvv"
                  v-model="registerForm.cardCvv"
                  type="password"
                  placeholder="1234"
                  class="form-input"
                  :class="{ 'error': errors.cardCvv }"
                  maxlength="4"
                  required
                />
              </div>
              <span v-if="errors.cardCvv" class="error-message">{{ errors.cardCvv }}</span>
            </div>
          </div>

          <!-- Terms and Conditions -->
          <div class="form-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="registerForm.agreeToTerms"
                class="checkbox-input"
                :class="{ 'error': errors.agreeToTerms }"
                required
              />
              <span class="checkbox-custom"></span>
              <span class="checkbox-text">
                By checking here you agree to our
                <a href="https://quali-d.com/privacy-policy" target="_blank" class="link">privacy policy</a>
                &
                <a href="https://quali-d.com/terms-conditions" target="_blank" class="link">terms & conditions</a>
              </span>
            </label>
            <span v-if="errors.agreeToTerms" class="error-message">{{ errors.agreeToTerms }}</span>
          </div>

          <button
            type="submit"
            class="register-button"
            :disabled="isLoading || !isFormValid"
            :class="{ 'loading': isLoading, 'disabled': !isFormValid }"
          >
            <span v-if="!isLoading">Create Account</span>
            <span v-else class="loading-spinner">
              <span class="spinner"></span>
              Creating account...
            </span>
          </button>

          <div v-if="registerError" class="register-error">
            {{ registerError }}
          </div>
        </form>

        <div class="form-footer">
          <p class="footer-text">
            Already have an account?
            <router-link to="/login" class="login-link">Sign In</router-link>
          </p>
        </div>
      </div>
    </div>

    <!-- OTP Modal -->
    <div v-if="showOtpModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">Verify WhatsApp OTP</h3>
          <button @click="closeOtpModal" class="modal-close">×</button>
        </div>

        <div class="modal-body">
          <p class="otp-instruction">
            We've sent a 6-digit verification code to your WhatsApp number:
            <strong>+{{ displayCountryCode }} {{ registerForm.phone }}</strong>
          </p>

          <div class="form-group">
            <label for="otpCode" class="form-label">Enter 6-digit OTP</label>
            <div class="input-wrapper">
              <input
                id="otpCode"
                v-model="otpCode"
                type="text"
                placeholder="123456"
                class="form-input otp-input"
                :class="{ 'error': otpError }"
                maxlength="6"
                @input="otpCode = otpCode.replace(/\D/g, '')"
                required
              />
            </div>
            <span v-if="otpError" class="error-message">{{ otpError }}</span>
          </div>
        </div>

        <div class="modal-footer">
          <button
            @click="closeOtpModal"
            class="modal-button secondary"
            :disabled="isVerifyingOtp"
          >
            Cancel
          </button>
          <button
            @click="verifyOtpAndPay"
            class="modal-button primary"
            :disabled="isVerifyingOtp || otpCode.length !== 6"
            :class="{ 'loading': isVerifyingOtp }"
          >
            <span v-if="!isVerifyingOtp">Verify & Proceed to Payment</span>
            <span v-else class="loading-spinner">
              <span class="spinner"></span>
              Verifying...
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DEFAULT_COUNTRY_CODE } from '@/utils/countryCodes'
import CountryCodeSelector from '@/components/CountryCodeSelector.vue'

export default {
  name: 'RegisterView',
  components: {
    CountryCodeSelector
  },
  data () {
    return {
      registerForm: {
        name: '',
        companyName: '',
        email: '',
        countryCode: DEFAULT_COUNTRY_CODE,
        phone: '',
        password: '',
        confirmPassword: '',
        selectedPlan: null,
        cardNumber: '',
        cardMonth: '',
        cardYear: '',
        cardCvv: '',
        agreeToTerms: false
      },
      errors: {},
      showPassword: false,
      showConfirmPassword: false,
      isLoading: false,
      registerError: '',
      plans: [],
      loadingPlans: true,
      plansError: '',
      selectedPlanType: 'annual',
      preSelectedPlanId: null,
      showOtpModal: false,
      otpCode: '',
      otpError: '',
      isVerifyingOtp: false,
      months: [
        { value: '01', label: '01 - January' },
        { value: '02', label: '02 - February' },
        { value: '03', label: '03 - March' },
        { value: '04', label: '04 - April' },
        { value: '05', label: '05 - May' },
        { value: '06', label: '06 - June' },
        { value: '07', label: '07 - July' },
        { value: '08', label: '08 - August' },
        { value: '09', label: '09 - September' },
        { value: '10', label: '10 - October' },
        { value: '11', label: '11 - November' },
        { value: '12', label: '12 - December' }
      ]
    }
  },
  computed: {
    filteredPlans () {
      return this.plans.filter(plan => plan.plan_type === this.selectedPlanType)
    },
    selectedPlanDetails () {
      return this.plans.find(plan => plan.id === this.registerForm.selectedPlan)
    },
    displayCountryCode () {
      // Special case for Egypt: display "+2" instead of "+20"
      return this.registerForm.countryCode === '20' ? '2' : this.registerForm.countryCode
    },
    passwordChecks () {
      const password = this.registerForm.password
      return {
        hasMinLength: password.length >= 8,
        hasUppercase: /[A-Z]/.test(password),
        hasLowercase: /[a-z]/.test(password),
        hasNumber: /[0-9]/.test(password)
      }
    },
    years () {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let i = 0; i < 10; i++) {
        const fullYear = currentYear + i
        const shortYear = fullYear.toString().slice(-2) // Get last 2 digits
        years.push({ value: shortYear, label: fullYear.toString() })
      }
      return years
    },
    isFormValid () {
      const form = this.registerForm
      const { hasMinLength, hasUppercase, hasLowercase, hasNumber } = this.passwordChecks

      // Generic phone validation - just check it has some digits
      const cleanedPhone = form.phone.replace(/\D/g, '')
      const isValidPhone = cleanedPhone.length >= 7 && cleanedPhone.length <= 15

      return form.name.trim() &&
             form.companyName.trim() &&
             form.email.trim() &&
             /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
             form.countryCode &&
             form.phone.trim() &&
             isValidPhone &&
             form.password &&
             hasMinLength && hasUppercase && hasLowercase && hasNumber &&
             form.confirmPassword &&
             form.password === form.confirmPassword &&
             form.selectedPlan &&
             form.cardNumber.trim() &&
             form.cardNumber.replace(/\s/g, '').length >= 13 &&
             form.cardMonth &&
             form.cardYear &&
             form.cardCvv &&
             form.cardCvv.length >= 3 && form.cardCvv.length <= 4 &&
             form.agreeToTerms
    }
  },
  methods: {
    togglePassword () {
      this.showPassword = !this.showPassword
    },
    toggleConfirmPassword () {
      this.showConfirmPassword = !this.showConfirmPassword
    },
    selectPlan (planId) {
      this.registerForm.selectedPlan = planId
      if (this.errors.selectedPlan) {
        delete this.errors.selectedPlan
      }
    },
    formatCardNumber () {
      let value = this.registerForm.cardNumber.replace(/\D/g, '')
      value = value.replace(/(\d{4})(?=\d)/g, '$1 ')
      this.registerForm.cardNumber = value
    },
    formatPhoneNumber (phoneNumber) {
      // Remove all non-digit characters
      const cleaned = phoneNumber.replace(/\D/g, '')

      // If number starts with 20 (Egypt country code), keep as is
      if (cleaned.startsWith('20')) {
        return cleaned
      }

      // If number starts with 01 (Egyptian mobile format), add country code
      if (cleaned.startsWith('01') && cleaned.length === 11) {
        return '20' + cleaned.substring(1) // Remove the leading 0 and add 20
      }

      // If number starts with 1 and is 10 digits (without the leading 0), add 20
      if (cleaned.startsWith('1') && cleaned.length === 10) {
        return '20' + cleaned
      }

      // If number is exactly 10 digits and starts with common Egyptian prefixes
      if (cleaned.length === 10 && /^[12][012345]/.test(cleaned)) {
        return '20' + cleaned
      }

      // If none of the above, return as is (might be invalid)
      return cleaned
    },
    generateRefNum () {
      // Generate a 16-digit random reference number
      let refNum = ''
      for (let i = 0; i < 16; i++) {
        refNum += Math.floor(Math.random() * 10).toString() // Append a random digit (0-9)
      }
      return parseInt(refNum, 10) // Convert the string to an integer and return it
    },
    async fetchPlans () {
      this.loadingPlans = true
      this.plansError = ''

      try {
        const response = await fetch('https://api.quali-d.com/plans')
        if (!response.ok) {
          throw new Error('Failed to fetch plans')
        }
        this.plans = await response.json()
      } catch (error) {
        console.error('Error fetching plans:', error)
        this.plansError = 'Failed to load plans. Please try again.'
      } finally {
        this.loadingPlans = false
      }
    },
    validateForm () {
      this.errors = {}
      let isValid = true

      // Name validation
      if (!this.registerForm.name.trim()) {
        this.errors.name = 'Full name is required'
        isValid = false
      }

      // Company name validation
      if (!this.registerForm.companyName.trim()) {
        this.errors.companyName = 'Company name is required'
        isValid = false
      }

      // Email validation
      if (!this.registerForm.email.trim()) {
        this.errors.email = 'Email is required'
        isValid = false
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.registerForm.email)) {
        this.errors.email = 'Please enter a valid email address'
        isValid = false
      }

      // Phone validation
      if (!this.registerForm.phone.trim()) {
        this.errors.phone = 'Phone number is required'
        isValid = false
      } else {
        const cleanedPhone = this.registerForm.phone.replace(/\D/g, '')
        // Generic phone validation - 7 to 15 digits
        if (cleanedPhone.length < 7 || cleanedPhone.length > 15) {
          this.errors.phone = 'Please enter a valid phone number (7-15 digits)'
          isValid = false
        }
      }

      // Country code validation
      if (!this.registerForm.countryCode) {
        this.errors.countryCode = 'Country code is required'
        isValid = false
      }

      // Password validation
      if (!this.registerForm.password) {
        this.errors.password = 'Password is required'
        isValid = false
      } else {
        const { hasMinLength, hasUppercase, hasLowercase, hasNumber } = this.passwordChecks
        if (!hasMinLength || !hasUppercase || !hasLowercase || !hasNumber) {
          this.errors.password = 'Password does not meet the requirements'
          isValid = false
        }
      }

      // Confirm password validation
      if (!this.registerForm.confirmPassword) {
        this.errors.confirmPassword = 'Please confirm your password'
        isValid = false
      } else if (this.registerForm.password !== this.registerForm.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match'
        isValid = false
      }

      // Plan selection validation
      if (!this.registerForm.selectedPlan) {
        this.errors.selectedPlan = 'Please select a plan'
        isValid = false
      }

      // Card number validation
      if (!this.registerForm.cardNumber.trim()) {
        this.errors.cardNumber = 'Card number is required'
        isValid = false
      } else if (this.registerForm.cardNumber.replace(/\s/g, '').length < 13) {
        this.errors.cardNumber = 'Please enter a valid card number'
        isValid = false
      }

      // Card month validation
      if (!this.registerForm.cardMonth) {
        this.errors.cardMonth = 'Expiry month is required'
        isValid = false
      }

      // Card year validation
      if (!this.registerForm.cardYear) {
        this.errors.cardYear = 'Expiry year is required'
        isValid = false
      }

      // CVV validation
      if (!this.registerForm.cardCvv) {
        this.errors.cardCvv = 'CVV is required'
        isValid = false
      } else if (this.registerForm.cardCvv.length < 3 || this.registerForm.cardCvv.length > 4) {
        this.errors.cardCvv = 'CVV must be 3-4 digits'
        isValid = false
      }

      // Terms and conditions validation
      if (!this.registerForm.agreeToTerms) {
        this.errors.agreeToTerms = 'You must agree to the terms and conditions'
        isValid = false
      }

      return isValid
    },
    async handleRegister () {
      this.registerError = ''

      if (!this.validateForm()) {
        return
      }

      this.isLoading = true

      try {
        // Special case for Egypt: use "+2" instead of "+20"
        const countryCode = this.registerForm.countryCode === '20' ? '2' : this.registerForm.countryCode

        // Send registration request to API
        const registrationData = {
          name: this.registerForm.name,
          company_name: this.registerForm.companyName,
          country_code: countryCode,
          phone: this.registerForm.phone,
          password: this.registerForm.password,
          confirm_password: this.registerForm.confirmPassword,
          email: this.registerForm.email
        }

        console.log('Sending registration data:', registrationData)

        const response = await fetch('https://api.quali-d.com/register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(registrationData)
        })

        const result = await response.json()
        console.log('Registration response:', result)

        // Check if the registration was successful
        if (!result.success) {
          throw new Error(result.message || 'Registration failed')
        }

        // Registration successful, show OTP modal
        this.showOtpModal = true
      } catch (error) {
        console.error('Registration error:', error)
        this.registerError = error.message || 'Registration failed. Please try again.'
      } finally {
        this.isLoading = false
      }
    },
    async verifyOtpAndPay () {
      this.otpError = ''
      this.isVerifyingOtp = true

      try {
        // Generate 16-digit random merchant reference number
        const merchantRefNum = this.generateRefNum()

        // Get selected plan details
        const selectedPlan = this.plans.find(plan => plan.id === this.registerForm.selectedPlan)

        // Special case for Egypt: use "+2" instead of "+20"
        const countryCode = this.registerForm.countryCode === '20' ? '2' : this.registerForm.countryCode

        const otpVerificationData = {
          name: this.registerForm.name.toString(),
          otp: this.otpCode.toString(),
          company_name: this.registerForm.companyName.toString(),
          country_code: countryCode,
          phone: this.registerForm.phone.toString(),
          password: this.registerForm.password.toString(),
          email: this.registerForm.email.toString(),
          amount: parseInt(selectedPlan.price, 10),
          cardNumber: this.registerForm.cardNumber.replace(/\s/g, '').toString(),
          expiryYear: this.registerForm.cardYear.toString(),
          expiryMonth: this.registerForm.cardMonth.toString(),
          cvv: this.registerForm.cardCvv.toString(),
          merchantRefNum: merchantRefNum, // Keep as integer
          plan_id: this.registerForm.selectedPlan.toString(),
          plan_description: selectedPlan.name.replace(/\s*\(\d+,?\d*\s*EGP\)/, '').toString() // Remove price from plan name
        }

        console.log('Sending OTP verification data:', otpVerificationData)
        console.log('JSON payload:', JSON.stringify(otpVerificationData, null, 2))

        const response = await fetch('https://api.quali-d.com/verify-otp', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(otpVerificationData)
        })

        const result = await response.json()
        console.log('OTP verification response:', result)
        console.log('Response status:', response.status)
        console.log('Response headers:', [...response.headers.entries()])

        if (!result.success) {
          console.error('API Error Details:', result)
          throw new Error(result.message || 'OTP verification failed')
        }

        if (result.redirect_url && result.merchant_ref_number) {
          // Store the API-returned merchant reference number for payment status checking
          localStorage.setItem('merchantRefNum', result.merchant_ref_number.toString())

          // Store login credentials temporarily for auto-login after successful payment
          // These will be cleared after login or if payment fails
          localStorage.setItem('pendingLoginCredentials', JSON.stringify({
            country_code: countryCode,
            phone: this.registerForm.phone,
            password: this.registerForm.password
          }))

          console.log('Using API merchant reference number:', result.merchant_ref_number)

          // Redirect to payment page with iframe
          this.$router.push({
            name: 'payment',
            query: {
              url: result.redirect_url,
              merchantRefNum: result.merchant_ref_number
            }
          })
        } else {
          throw new Error('No payment URL or merchant reference number received')
        }
      } catch (error) {
        console.error('OTP verification error:', error)
        this.otpError = error.message || 'OTP verification failed. Please try again.'
      } finally {
        this.isVerifyingOtp = false
      }
    },
    closeOtpModal () {
      this.showOtpModal = false
      this.otpCode = ''
      this.otpError = ''
    }
  },
  mounted () {
    this.fetchPlans()

    // Check for plan parameter in URL
    const urlParams = new URLSearchParams(window.location.search)
    const planParam = urlParams.get('plan')
    if (planParam) {
      this.preSelectedPlanId = parseInt(planParam)
    }
  },
  watch: {
    plans (newPlans) {
      // When plans are loaded, check if we need to pre-select a plan from URL
      if (this.preSelectedPlanId && newPlans.length > 0) {
        const planExists = newPlans.find(plan => plan.id === this.preSelectedPlanId)
        if (planExists) {
          this.registerForm.selectedPlan = this.preSelectedPlanId
          // Switch toggle to match the plan type
          this.selectedPlanType = planExists.plan_type
          console.log('Pre-selected plan:', this.preSelectedPlanId, 'Type:', planExists.plan_type)
        }
        this.preSelectedPlanId = null // Clear after use
      }
    }
  }
}
</script>

<style scoped>
.register-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  position: relative;
  overflow: hidden;
  padding: 20px 0;
}

.register-form-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 48px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 800px;
  margin: 20px;
  position: relative;
  z-index: 10;
}

.logo-section {
  text-align: center;
  margin-bottom: 32px;
}

.logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 8px;
}

.logo {
  height: 40px;
  width: auto;
}

.brand-subtitle {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
  font-weight: 500;
}

.form-header {
  text-align: center;
  margin-bottom: 32px;
}

.form-title {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.form-section-title {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 32px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
}

.form-section-title:first-of-type {
  margin-top: 0;
}

.register-form {
  width: 100%;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  width: 18px;
  height: 18px;
  opacity: 0.6;
  z-index: 2;
  color: #6b7280;
}

.form-input {
  width: 100%;
  padding: 12px 16px 12px 52px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  color: #1f2937;
  transition: all 0.2s;
  outline: none;
}

.form-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.toggle-password {
  position: absolute;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  padding: 4px;
  border-radius: 4px;
  transition: opacity 0.2s;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-password:hover {
  opacity: 1;
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.field-note {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  margin-bottom: 0;
}

.password-requirements {
  margin-top: 8px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.requirements-title {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.requirements-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.requirements-list li {
  font-size: 12px;
  color: #ef4444;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.requirements-list li.valid {
  color: #059669;
}

.check-icon {
  font-weight: bold;
  width: 12px;
  text-align: center;
}

.loading-plans {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  color: #6b7280;
}

.plans-error {
  text-align: center;
  padding: 24px;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.retry-button {
  margin-top: 12px;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

/* Plan Toggle Styles */
.plan-toggle-container {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 6px;
  background: #f3f4f6;
  border-radius: 12px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.plan-toggle-button {
  flex: 1;
  padding: 12px 24px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
}

.plan-toggle-button:hover {
  color: #374151;
}

.plan-toggle-button.active {
  background: white;
  color: #1f2937;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.toggle-badge {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.plan-card {
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
  position: relative;
  overflow: visible;
}

.plan-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.plan-card.selected {
  border-color: #3b82f6;
  border-width: 3px;
  background: #eff6ff;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
  transform: scale(1.05);
}

.plan-card.selected::before {
  content: '✓';
  position: absolute;
  top: 12px;
  left: 12px;
  width: 28px;
  height: 28px;
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  z-index: 5;
}

/* Annual Plan Highlighting */
.plan-card.annual-plan {
  border-color: #8b5cf6;
  background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
}

.plan-card.annual-plan:hover {
  border-color: #7c3aed;
  box-shadow: 0 6px 16px rgba(139, 92, 246, 0.3);
  transform: translateY(-2px);
}

.plan-card.annual-plan.selected {
  border-color: #7c3aed;
  border-width: 3px;
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  box-shadow: 0 8px 24px rgba(139, 92, 246, 0.5);
  transform: scale(1.05);
}

.plan-card.annual-plan.selected::before {
  background: #7c3aed;
}

/* Annual Badge */
.annual-badge {
  position: absolute;
  top: -10px;
  right: -10px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
  z-index: 10;
  animation: pulse 2s ease-in-out infinite;
}

.star-icon {
  font-size: 13px;
  line-height: 1;
}

.badge-text {
  letter-spacing: 0.5px;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
  }
  50% {
    box-shadow: 0 4px 20px rgba(245, 158, 11, 0.6);
  }
}

/* Plan Duration Text */
.plan-duration {
  margin-top: 6px;
}

.duration-text {
  font-size: 12px;
  color: #8b5cf6;
  font-weight: 600;
  background: rgba(139, 92, 246, 0.1);
  padding: 4px 12px;
  border-radius: 12px;
  display: inline-block;
}

.plan-header {
  text-align: center;
  margin-bottom: 16px;
}

.plan-name {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.plan-price {
  font-size: 20px;
  font-weight: 700;
  color: #3b82f6;
}

/* Annual Plan Price Display */
.price-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.monthly-price {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.price-amount {
  font-size: 32px;
  font-weight: 700;
  color: #8b5cf6;
  line-height: 1;
}

.price-currency {
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
}

.annual-price {
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
}

/* Plan Summary Section */
.plan-summary {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #3b82f6;
  border-radius: 12px;
  padding: 20px;
  margin: 24px 0;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1e40af;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 16px;
}

.summary-header svg {
  stroke: #3b82f6;
}

.summary-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.summary-left {
  flex: 1;
}

.summary-plan-name {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 6px 0;
}

.summary-billing {
  display: flex;
  gap: 4px;
}

.billing-info {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.summary-right {
  text-align: right;
}

.summary-price {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.total-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.total-amount {
  font-size: 24px;
  font-weight: 700;
  color: #3b82f6;
}

.plan-details {
  space-y: 8px;
}

.plan-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
}

.detail-label {
  color: #6b7280;
}

.detail-value {
  font-weight: 600;
  color: #374151;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1.5;
}

.checkbox-input {
  display: none;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
  margin-top: 2px;
}

.checkbox-input:checked + .checkbox-custom {
  background: #3b82f6;
  border-color: #3b82f6;
}

.checkbox-input:checked + .checkbox-custom:after {
  content: '✓';
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.checkbox-input.error + .checkbox-custom {
  border-color: #ef4444;
}

.checkbox-text {
  color: #374151;
}

.link {
  color: #3b82f6;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.register-button {
  width: 100%;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
  margin-top: 24px;
}

.register-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
}

.register-button:disabled,
.register-button.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  background: #9ca3af;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.register-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 16px;
  text-align: center;
}

.form-footer {
  text-align: center;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.footer-text {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
}

.login-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 600;
}

.login-link:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .register-form-container {
    padding: 32px 24px;
    margin: 16px;
  }

  .plans-grid {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 16px;
  padding: 32px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 8px;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  margin-bottom: 32px;
}

.otp-instruction {
  color: #6b7280;
  font-size: 16px;
  margin-bottom: 24px;
  line-height: 1.5;
}

.otp-input {
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0.1em;
  padding: 16px 20px !important;
}

.modal-footer {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
}

.modal-button {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  position: relative;
}

.modal-button.secondary {
  background: #f3f4f6;
  color: #374151;
}

.modal-button.secondary:hover:not(:disabled) {
  background: #e5e7eb;
}

.modal-button.primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.modal-button.primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.modal-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.modal-button.primary:disabled {
  background: #9ca3af;
}

@media (max-width: 640px) {
  .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .checkbox-label {
    font-size: 13px;
  }

  .modal-container {
    margin: 16px;
    padding: 24px;
  }

  .modal-footer {
    flex-direction: column-reverse;
  }

  .modal-button {
    width: 100%;
  }
}

/* Phone Input Group Styles */
.phone-input-group {
  display: flex;
  gap: 8px;
  width: 100%;
}

.country-selector-wrapper {
  flex-shrink: 0;
}

.country-select {
  width: 120px;
  padding: 12px 8px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  color: #1f2937;
  transition: all 0.2s;
  outline: none;
  cursor: pointer;
  height: 48px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", "Apple Color Emoji", Arial, sans-serif;
}

.country-select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.country-select.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.phone-input-wrapper {
  flex: 1;
}

.phone-input-wrapper .form-input {
  width: 100%;
}

@media (max-width: 480px) {
  .country-select {
    width: 100px;
    font-size: 13px;
  }
}
</style>
